/**
 * Turning a URL the desktop shell hands us into a route in this app.
 *
 * Two things arrive this way: a custom-scheme deep link the OS routed to the
 * app (`walkingonearth://…`), and the `deepLink` echoed back when the user
 * clicks a notification this app posted.
 */

/** Where a notification click lands when it names nothing more specific. */
export const DEFAULT_DEEP_LINK_ROUTE = "/user/home";

/**
 * The in-app path for a link, or `null` for one this app has no route for.
 *
 * Handles three shapes:
 *
 * - An app-relative path (`/user/home`), which is what this app's own
 *   notifications carry, returned as-is.
 * - An OAuth callback (`walkingonearth://oauth-callback?code=…`), mapped onto
 *   the SSO landing route so the existing callback screen does the exchange.
 *   This is how every desktop SSO sign-in returns (see `sso.ts`). Matched on
 *   the target rather than the scheme, because the scheme differs per
 *   environment.
 * - Any other custom-scheme link, whose target becomes the path.
 *
 * An http(s) URL returns `null`. Navigating the app to an arbitrary web page
 * on the strength of a link the OS handed us is not something this function
 * will authorise.
 */
export function routeForDeepLink(url: string): string | null {
  if (url.startsWith("/")) return url;

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // A malformed link is the OS handing us something we did not register.
    // There is nothing to route and nothing to tell the user.
    return null;
  }

  if (parsed.protocol === "http:" || parsed.protocol === "https:") return null;

  // A custom-scheme URL parses as opaque, so the first path segment lands in
  // `host` and the rest in `pathname`: `walkingonearth://user/home` gives
  // `host` "user" and `pathname` "/home". Joining them back is what keeps a
  // multi-segment target intact.
  const target = `${parsed.host}${parsed.pathname}`.replace(/^\/+/, "").replace(/\/+$/, "");
  if (target === "") return null;

  if (target === "oauth-callback") {
    if (!parsed.searchParams.get("code") && !parsed.searchParams.get("error")) {
      return null;
    }
    return `/auth/callback${parsed.search}`;
  }

  return `/${target}${parsed.search}`;
}
